import { useTranslation } from 'react-i18next';
// frontend/src/hooks/useWebSocket.js
import { useEffect, useState, useCallback, useRef } from 'react';
import websocketService from '../services/websocketService';
import { api } from '../services/api';

/**
 * Hook principal para conexión WebSocket
 */
export function useWebSocket() {
  const { t } = useTranslation();
  const [isConnected, setIsConnected] = useState(websocketService.isConnected);
  const [connectionId, setConnectionId] = useState(websocketService.connectionId);

  useEffect(() => {
    const handleConnected = (data) => {
      setIsConnected(true);
      setConnectionId(data?.connectionId || websocketService.connectionId);
    };

    const handleDisconnected = () => {
      setIsConnected(false);
      setConnectionId(null);
    };

    const unsubConnected = websocketService.on('connected', handleConnected);
    const unsubDisconnected = websocketService.on('disconnected', handleDisconnected);

    setIsConnected(websocketService.isConnected);
    setConnectionId(websocketService.connectionId);

    return () => {
      unsubConnected();
      unsubDisconnected();
    };
  }, []);

  const connect = useCallback((token) => {
    websocketService.connect(token);
  }, []);

  const disconnect = useCallback(() => {
    websocketService.disconnect();
  }, []);

  return {
    isConnected,
    connectionId,
    connect,
    disconnect,
    service: websocketService
  };
}

/**
 * Hook para suscribirse a un vehículo específico
 */
export function useVehicleSubscription(vehicleId) {
  const { t } = useTranslation();
  const [vehicleData, setVehicleData] = useState(null);
  const [lastUpdate, setLastUpdate] = useState(null);

  useEffect(() => {
    if (!vehicleId) return;

    websocketService.subscribeToVehicle(vehicleId);

    const handleTelemetryUpdate = (data) => {
      if (data.vehicle_id === vehicleId || String(data.vehicle_id) === String(vehicleId)) {
        const timestamp = Date.now();
        
        setVehicleData(prev => {
          const newData = {};
          
          if (prev) {
            Object.keys(prev).forEach(key => {
              newData[key] = prev[key];
            });
          }
          
          newData[data.position] = {
            pressure: data.pressure,
            temperature: data.temperature,
            timestamp: data.timestamp,
            dataAge: data.dataAge,
            isRealtime: true,
            _updateTimestamp: timestamp
          };
          
          return newData;
        });
        
        setLastUpdate(new Date());
      }
    };

    const handleVehicleDataUpdate = (data) => {
      if (data.vehicle_id === vehicleId || String(data.vehicle_id) === String(vehicleId)) {
        const timestamp = Date.now();
        
        setVehicleData({
          ...data,
          _updateTimestamp: timestamp
        });
        setLastUpdate(new Date());
      }
    };

    const unsubTelemetry = websocketService.on('telemetry_update', handleTelemetryUpdate);
    const unsubVehicleData = websocketService.on('vehicle_data_update', handleVehicleDataUpdate);

    return () => {
      websocketService.unsubscribeFromVehicle(vehicleId);
      unsubTelemetry();
      unsubVehicleData();
    };
  }, [vehicleId]);

  return { vehicleData, lastUpdate };
}

/**
 * 🔥 Hook para suscribirse a múltiples vehículos - CON DEBUGGING COMPLETO
 */
export function useVehiclesSubscription(vehicleIds) {
  const { t } = useTranslation();
  const [vehiclesData, setVehiclesData] = useState({});
  const [lastUpdate, setLastUpdate] = useState(null);
  const [updateCounter, setUpdateCounter] = useState(0);
  const previousIds = useRef([]);
  // Track subscribed IDs separately for safe unmount cleanup
  const subscribedIdsRef = useRef([]);

  // Unmount-only cleanup: unsubscribe all remaining vehicles
  useEffect(() => {
    return () => {
      subscribedIdsRef.current.forEach(id => websocketService.unsubscribeFromVehicle(id));
      subscribedIdsRef.current = [];
    };
  }, []);

  useEffect(() => {
    if (!vehicleIds || vehicleIds.length === 0) {
      return;
    }

    const currentIds = vehicleIds.map(String);
    const prevIds = previousIds.current.map(String);

    const newIds = currentIds.filter(id => !prevIds.includes(id));
    const removedIds = prevIds.filter(id => !currentIds.includes(id));

    if (newIds.length > 0) {
      websocketService.subscribeToVehicles(newIds);
      subscribedIdsRef.current = [...new Set([...subscribedIdsRef.current, ...newIds])];
    }

    removedIds.forEach(id => {
      websocketService.unsubscribeFromVehicle(id);
      subscribedIdsRef.current = subscribedIdsRef.current.filter(sid => sid !== id);
    });

    previousIds.current = [...vehicleIds];

    const handleTelemetryUpdate = (data) => {
      const vehicleIdStr = String(data.vehicle_id);
      
      if (!currentIds.includes(vehicleIdStr)) {
        return;
      }

      const timestamp = Date.now();

      setVehiclesData(prev => {
        const updated = Object.assign({}, prev);
        const vehicleData = Object.assign({}, updated[vehicleIdStr] || {});

        vehicleData[data.position] = {
          pressure: data.pressure,
          temperature: data.temperature,
          timestamp: data.timestamp,
          dataAge: data.dataAge,
          sensor_id: data.sensor_id,
          isRealtime: true,
          _updateTimestamp: timestamp
        };

        updated[vehicleIdStr] = vehicleData;
        
        return updated;
      });

      setLastUpdate(new Date());
      setUpdateCounter(c => c + 1);
    };

    const handleTelemetryBatch = (data) => {
      const timestamp = Date.now();

      setVehiclesData(prev => {
        const updated = Object.assign({}, prev);

        Object.entries(data).forEach(([vehicleId, positions]) => {
          const vehicleData = Object.assign({}, updated[vehicleId] || {});

          Object.entries(positions).forEach(([position, posData]) => {
            vehicleData[position] = {
              ...posData,
              isRealtime: true,
              _updateTimestamp: timestamp
            };
          });

          updated[vehicleId] = vehicleData;
        });
        return updated;
      });

      setLastUpdate(new Date());
      setUpdateCounter(c => c + 1);
    };

    const handleVehicleDataUpdate = (data) => {
      const vehicleIdStr = String(data.vehicle_id);
      if (!currentIds.includes(vehicleIdStr)) {
        return;
      }

      const timestamp = Date.now();

      setVehiclesData(prev => {
        const updated = Object.assign({}, prev);

        const realtimeData = {};
        Object.entries(data.realtime_data || {}).forEach(([pos, posData]) => {
          realtimeData[pos] = {
            ...posData,
            isRealtime: true,
            _updateTimestamp: timestamp
          };
        });

        updated[vehicleIdStr] = realtimeData;
        return updated;
      });

      setLastUpdate(new Date());
      setUpdateCounter(c => c + 1);
    };

    const unsubUpdate = websocketService.on('telemetry_update', handleTelemetryUpdate);
    const unsubBatch = websocketService.on('telemetry_batch', handleTelemetryBatch);
    const unsubVehicleData = websocketService.on('vehicle_data_update', handleVehicleDataUpdate);

    return () => {
      unsubUpdate();
      unsubBatch();
      unsubVehicleData();
      // Note: vehicle unsubscription is handled by the diff logic (removedIds)
      // and the unmount-only cleanup effect above. We do NOT unsubscribe here
      // because React Strict Mode / vehicleIds reference changes would cause all
      // vehicles to be unsubscribed and never re-subscribed (newIds would be empty).
    };
  }, [vehicleIds]);

  return { 
    vehiclesData, 
    lastUpdate,
    updateCounter
  };
}

/**
 * Hook para alarmas en tiempo real
 */
export function useAlarmSubscription() {
  const { t } = useTranslation();
  const [alarms, setAlarms] = useState([]);
  const [activeAlarmCount, setActiveAlarmCount] = useState(0);
  const [lastAlarm, setLastAlarm] = useState(null);

  useEffect(() => {
    const handleAlarmTriggered = (data) => {
      console.warn(t('hardcoded.hooks_usewebsocket.alarm_received'), data);

      const timestamp = Date.now();

      setAlarms(prev => {
        const newAlarms = [...prev];

        const existingIndex = newAlarms.findIndex(
          a => a.vehicle_id === data.vehicle_id &&
            a.position === data.position &&
            a.metric_type === data.metric_type
        );

        if (existingIndex >= 0) {
          newAlarms[existingIndex] = {
            ...data,
            updated_at: new Date(),
            _updateTimestamp: timestamp
          };
        } else {
          newAlarms.push({
            ...data,
            id: `${data.vehicle_id}-${data.position}-${data.metric_type}`,
            _updateTimestamp: timestamp
          });
        }

        return newAlarms;
      });

      setLastAlarm(data);
      setActiveAlarmCount(prev => prev + 1);
    };

    const handleAlarmCleared = (data) => {
      setAlarms(prev => (Array.isArray(prev) ? prev : []).filter(
        a => a && !(a.vehicle_id === data.vehicle_id &&
          a.position === data.position &&
          a.metric_type === data.metric_type)
      ));
      setActiveAlarmCount(prev => Math.max(0, prev - 1));
    };

    const handleAlarmStatusBatch = (data) => {
      const statuses = data.statuses || data;
      if (Array.isArray(statuses)) {
        const timestamp = Date.now();
        const newAlarms = statuses.map(alarm => ({
          ...alarm,
          _updateTimestamp: timestamp
        }));

        setAlarms(newAlarms);
        setActiveAlarmCount((data.critical_count || 0) + (data.warning_count || 0));
      }
    };

    const unsubTriggered = websocketService.on('alarm_triggered', handleAlarmTriggered);
    const unsubCleared = websocketService.on('alarm_cleared', handleAlarmCleared);
    const unsubBatch = websocketService.on('alarm_status_update', handleAlarmStatusBatch);
    const unsubBatch2 = websocketService.on('alarm_status_batch', handleAlarmStatusBatch);

    return () => {
      unsubTriggered();
      unsubCleared();
      unsubBatch();
      unsubBatch2();
    };
  }, []);

  const clearAlarm = useCallback((alarmId) => {
    setAlarms(prev => (Array.isArray(prev) ? prev : []).filter(a => a && a.id !== alarmId));
  }, []);

  return { alarms, activeAlarmCount, lastAlarm, clearAlarm };
}

/**
 * Hook para tickets de alarma
 */
export function useTicketSubscription() {
  const { t } = useTranslation();
  const [tickets, setTickets] = useState([]);
  const [openTicketCount, setOpenTicketCount] = useState(0);
  const [requiresAudio, setRequiresAudio] = useState(false);
  const [lastTicket, setLastTicket] = useState(null);

  useEffect(() => {
    const handleTicketCreated = (data) => {
      console.warn(t('hardcoded.hooks_usewebsocket.new_ticket'), data);

      const timestamp = Date.now();

      setTickets(prev => {
        const exists = prev.some(t => t.ticket_id === data.ticket_id);
        if (exists) return prev;

        return [...prev, { ...data, _updateTimestamp: timestamp }];
      });

      setLastTicket(data);

      if (data.status === 'open') {
        setOpenTicketCount(prev => prev + 1);
        setRequiresAudio(true);
      }
    };

    const handleTicketAcknowledged = (data) => {
      const timestamp = Date.now();

      setTickets(prev => {
        const updated = prev.map(t =>
          t.ticket_id === data.ticket_id
            ? {
              ...t,
              status: 'acknowledged',
              acknowledged_at: data.acknowledged_at,
              _updateTimestamp: timestamp
            }
            : t
        );

        const stillOpen = updated.some(t => t.status === 'open');
        setRequiresAudio(stillOpen);
        setOpenTicketCount(updated.filter(t => t.status === 'open').length);

        return updated;
      });
    };

    const handleTicketResolved = (data) => {
      setTickets(prev => {
        const updated = prev.filter(t => t.ticket_id !== data.ticket_id);

        const stillOpen = updated.some(t => t.status === 'open');
        setRequiresAudio(stillOpen);
        setOpenTicketCount(updated.filter(t => t.status === 'open').length);

        return updated;
      });
    };

    const handleActiveTicketsUpdate = (data) => {
      const ticketsList = data.tickets || data;
      if (Array.isArray(ticketsList)) {
        const timestamp = Date.now();
        const newTickets = ticketsList.map(ticket => ({
          ...ticket,
          _updateTimestamp: timestamp
        }));

        setTickets(newTickets);
        setOpenTicketCount(data.open_count || ticketsList.filter(t => t.status === 'open').length);
        setRequiresAudio(data.requires_audio || ticketsList.some(t => t.status === 'open'));
      }
    };

    const unsubCreated = websocketService.on('ticket_created', handleTicketCreated);
    const unsubAck = websocketService.on('ticket_acknowledged', handleTicketAcknowledged);
    const unsubResolved = websocketService.on('ticket_resolved', handleTicketResolved);
    const unsubActive = websocketService.on('active_tickets_update', handleActiveTicketsUpdate);

    // ESTADO INICIAL desde la API.
    // El WebSocket solo notifica CAMBIOS (creado / visto / resuelto) y nadie
    // emite `active_tickets_update` al conectar. Sin esta siembra, al cargar la
    // página la lista quedaba vacía aunque hubiera alarmas abiertas: el panel
    // no las contaba y la alarma sonora nunca arrancaba hasta que entrara un
    // ticket NUEVO. Se recarga también al reconectar, por si hubo cambios
    // mientras el socket estuvo caído.
    let cancelled = false;
    const seedFromApi = () => {
      api.alarmTickets.getActiveForAudio()
        .then((res) => {
          if (cancelled) return;
          const list = Array.isArray(res.data) ? res.data : [];
          const timestamp = Date.now();
          setTickets(list.map((ticket) => ({ ...ticket, _updateTimestamp: timestamp })));
          const open = list.filter((ticket) => ticket.status === 'open');
          setOpenTicketCount(open.length);
          setRequiresAudio(open.length > 0);
        })
        .catch(() => { /* sin semilla el WebSocket sigue actualizando */ });
    };
    seedFromApi();
    const unsubConnected = websocketService.on('connection_established', seedFromApi);

    return () => {
      cancelled = true;
      unsubCreated();
      unsubAck();
      unsubResolved();
      unsubActive();
      unsubConnected?.();
    };
  }, []);

  return { tickets, openTicketCount, requiresAudio, lastTicket };
}

/**
 * Hook genérico para eventos WebSocket personalizados
 */
export function useWebSocketEvent(eventName, handler) {
  const { t } = useTranslation();
  const handlerRef = useRef(handler);

  useEffect(() => {
    handlerRef.current = handler;
  }, [handler]);

  useEffect(() => {
    if (!eventName) return;

    const wrappedHandler = (data) => {
      if (handlerRef.current) {
        handlerRef.current(data);
      }
    };

    const unsub = websocketService.on(eventName, wrappedHandler);

    return () => {
      unsub();
    };
  }, [eventName]);
}

/**
 * Hook para estado de conexión
 */
export function useWebSocketStatus() {
  const { t } = useTranslation();
  const [status, setStatus] = useState(websocketService.getStatus());

  useEffect(() => {
    const updateStatus = () => setStatus(websocketService.getStatus());

    const unsubConnected = websocketService.on('connected', updateStatus);
    const unsubDisconnected = websocketService.on('disconnected', updateStatus);

    return () => {
      unsubConnected();
      unsubDisconnected();
    };
  }, []);

  return status;
}

export default useWebSocket;